// src/components/ProductionSteps.jsx
import React, { useState, useEffect } from "react";
import ProductionStepsTableTemplate from "./ProductionStepsTableTemplate"; // Assuming you have a table component
import AddEditProductionSteps from "./AddEditProductionSteps";
import { Button, Box, Drawer, Typography, CircularProgress } from "@mui/material";
import axios from "axios";
import env from "react-dotenv";

const ProductionSteps = () => {
    const [data, setData] = useState([]);
    const [open, setOpen] = useState(false);
    const [editData, setEditData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [expandedDrawer, setExpandedDrawer] = useState(false);
    const [productNames, setProductNames] = useState({});

    useEffect(() => {
        fetchData();
        fetchProductNames();
    }, []);

    const fetchData = async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_PJCBACKEND_URL}/api/production-steps`);
            setData(response.data[0]);
        } catch (error) {
            console.error("Error fetching data:", error);
            setError("Failed to fetch production steps data.");
        } finally {
            setLoading(false);
        }
    };

    const fetchProductNames = async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_PJCBACKEND_URL}/api/main-product`);
            const products = response.data;
            const productMap = {};
            products.forEach(product => {
                productMap[product.id] = product.product_name;
            });
            setProductNames(productMap);
        } catch (error) {
            console.error("Error fetching product names:", error);
        }
    };

    const handleAdd = () => {
        setEditData(null);
        setOpen(true);
        setExpandedDrawer(false);
    };

    const handleEdit = (item) => {
        setEditData(item);
        setOpen(true);
        setExpandedDrawer(false);
    };

    const handleDelete = async (step_id) => {
        try {
            await axios.delete(`${process.env.REACT_APP_PJCBACKEND_URL}/api/production-steps/${step_id}`);
            setData(data.filter(item => item.step_id !== step_id));
        } catch (error) {
            console.error("Error deleting record:", error);
        }
    };

    const handleClose = () => {
        setOpen(false);
        setExpandedDrawer(false);
    };

    const handleSave = async (item) => {
        try {
            // console.log("Saving Production Step data:", item);
            
            // Create a new object with only the relevant fields for the production_steps table
            const { product_name, ...dataToSave } = item; // Destructure to get product_name and MachineName
    
            // Create the final payload for the production_steps table
            const finalDataToSave = {
                product_id: dataToSave.product_id,
                dept_id: dataToSave.dept_id,
                process_id: dataToSave.process_id,
                step_name: dataToSave.step_name,
                step_order: dataToSave.step_order,
                dept_name: dataToSave.dept_name,
                departments: item.departments // Ensure this is included
                // Include any other fields that are relevant to the production_steps table
            };
    
            if (item.step_id) {
                await axios.put(`${process.env.REACT_APP_PJCBACKEND_URL}/api/production-steps/${item.step_id}`, finalDataToSave);
            } else {
                await axios.post(`${process.env.REACT_APP_PJCBACKEND_URL}/api/production-steps`, finalDataToSave);
            }
            fetchData();
        } catch (error) {
            console.error("Error saving record:", error);
        }
        setOpen(false);
        setExpandedDrawer(false); // Reset drawer expansion on save
    };

    const handleExpandDrawer = () => {
        setExpandedDrawer(true); // Set to true when expanding the drawer
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
                <Typography variant="h6" color="error">No records found</Typography>
            </Box>
        );
    }

    const transformedData = data.map(item => ({
        ...item,
        product_name: productNames[item.product_id] || ""
    }));

    return (
        <Box
        sx={{
            height: 670,
            width: '85%',
            marginLeft:"210px",
            marginRight:"150px",
          }}
        >
            <ProductionStepsTableTemplate
                columns={["product_name", "dept_name", "step_name", "step_order"]}
                headers={{
                    columns: ["Product Name", "Section Name", "Process/Step Name", "Step Order"]
                }}
                iterate={transformedData}
                data={transformedData}
                onDelete={handleDelete}
                onEdit={handleEdit}
                onAdd={handleAdd}
                add
                edit
            />
            <Drawer
                anchor="right"
                open={open}
                onClose={handleClose}
                sx={{
                    "& .MuiDrawer-paper": { width: expandedDrawer ? "800px" : "600px" } // Expand when form is open
                }}
            >
                <AddEditProductionSteps 
                    onClose={handleClose} 
                    onSave={handleSave} 
                    editData={editData} 
                    onExpandDrawer={handleExpandDrawer} // Pass the expand function to the child component 
                />
            </Drawer>
        </Box>
    );
};

export default ProductionSteps;